import { useState, useEffect } from 'react';
import { City, Collection, Country, FeaturedCollectionOptions } from 'src/types';
import { UrlHelper } from 'src/lib/routes';
import useQuery from './useQuery';
import useSearchBarState from './useSearchBarState';
import { generateArrayDates } from 'src/helpers';
import { useRouter } from 'next/router';
import Page from 'src/pages/better-for-local-communities';

function useFetchGroupOptions<T extends { id: number }>(opts: {
  route: UrlHelper;
  record: City | Collection | Country;
  initialData: {
    collections: T[];
    collectionOptions: FeaturedCollectionOptions;
  };
}) {
  const [record, setRecord] = useState(opts.record)
  const [data, setData] = useState(opts.initialData);
  const router = useRouter();
  const [, , initialSearchParams] = useSearchBarState();
  const isSearchParamsChanged = initialSearchParams.numGuest || initialSearchParams.dates.length > 0
  const { data: fetchData, isLoading, mutate } = useQuery<typeof data>(
    opts
      .route(
        { id: opts.record.slug },
        {
          start_date:
            initialSearchParams.dates.length === 2
              ? generateArrayDates(initialSearchParams.dates[0], initialSearchParams.dates[1])
              : initialSearchParams.dates,
          qty: initialSearchParams.numGuest,
          limit: isSearchParamsChanged ? 6 : undefined,
          offset: isSearchParamsChanged ? 0 : undefined,
        }
      )
    .toUrl()
  );

  const { collections, collectionOptions } = fetchData || {};

  useEffect(() => {
    if (record.id !== opts.record.id) {
      setRecord(opts.record)
      setData(opts.initialData);
    }
  }, [record.id, opts.record.id, router.pathname])


  useEffect(() => {
    if (!collections) return;
    if (!collectionOptions) return;
    const tmpCollections = collections as T[]
    if (isSearchParamsChanged) {
      setData(() => ({
        collections: tmpCollections,
        collectionOptions,
      }));
    } else {
      setData((prev) => {
        return {
          collections: [...prev.collections, ...tmpCollections],
          collectionOptions: {
            ...prev.collectionOptions,
            ...collectionOptions,
          },
        }
      });
    }
  }, [
    collections ? collections.map((v) => v.id).join(',') : 1,
    isSearchParamsChanged
  ]);

  return { ...data, isLoading }
}

export default useFetchGroupOptions;
